<template>
  <div
    class="message-panel-container"
    :class="{ 'fixed-container': isFixedPanel, 'profile-container': type === 'profile' }"
  >
    <div class="ibox message-panel" :class="[{ fixed: isFixedPanel }, getColumnLayoutClass]">
      <div class="ibox-title" :style="{ 'border-top': `2px solid ${panelColor}` }">
        <div class="drag-indicator" v-if="!isFixedPanel">
          <i class="fa fa-reorder"></i>
        </div>
        <h5>{{ title }}</h5>
        <div class="ibox-tools">
          <!-- &nbsp; -->
          <span class="locked" v-if="isLocked">
            <i class="fa fa-lock"></i>
          </span>
          <el-tooltip v-if="hasNewMessages && lastScrollPosition > 0" content="New Message(s)">
            <el-button type="text" :disabled="isLocked" @click="scrollNewMessages">
              <el-badge is-dot class="new-message">
                <i class="fa fa-envelope"></i>
              </el-badge>
            </el-button>
          </el-tooltip>
          <el-tooltip
            v-if="scrolling && !isLocked"
            content="Scroll to top"
            effect="dark"
            placement="bottom"
          >
            <el-button type="text" @click="scrollToTop" class="fa fa-arrow-up" />
          </el-tooltip>
          <el-tooltip
            v-if="!isLocked && type !== 'onair' && type !== 'twittertrends' && hasSearch"
            :content="`${showPanelSearch ? 'Close Search' : 'Search Panel'}`"
            effect="dark"
            placement="bottom"
          >
            <el-button type="text" @click="toggleSearch" :icon="getSearchBtnIcon('search')" />
          </el-tooltip>
          <el-tooltip
            v-if="!isLocked && type !== 'onair' && type !== 'twittertrends' && hasSearch"
            :content="`${showMediaSearch ? 'Close Media Search' : 'Search by Media'}`"
            effect="dark"
            placement="bottom"
          >
            <el-button type="text" @click="toggleMediaSearch" :icon="getSearchBtnIcon('media')" />
          </el-tooltip>
          <el-tooltip v-if="showSettings" content="Panel settings" effect="dark" placement="bottom">
            <el-button type="text" @click="$emit('showSettings')">
              <i class="fa fa-wrench"></i>
            </el-button>
          </el-tooltip>
          <el-tooltip
            v-if="showClose && !settings.instant_poll && panelLocker === 'auto'"
            content="Close panel"
            effect="dark"
            placement="bottom"
          >
            <el-button type="text" @click="$emit('closePanel')">
              <i class="fa fa-times"></i>
            </el-button>
          </el-tooltip>
        </div>

        <div v-show="showPanelSearch" class="panel-search">
          <el-input
            placeholder="Search"
            size="mini"
            style="margin: 5px auto"
            v-model="searchCriteria"
            @keydown.enter.native="searchPanelInteractions"
          >
            <i slot="prefix" class="el-icon-search el-input__icon"></i>
            <el-button slot="append" icon="fa fa-search" @click="searchPanelInteractions" />
          </el-input>
        </div>

        <div class="panel-search" v-if="showMediaSearch">
          <div class="clearfix"></div>
          <p style="font-size: 0.8em">Use these options to search by specific media types.</p>
          <div class="media-search">
            <el-checkbox-group class="media-search-chk" size="mini" v-model="mediaSearchCriteria">
              <el-tooltip v-for="item in mediaTypes" :key="item" :content="item" placement="bottom">
                <el-checkbox-button :label="item">
                  <i class="fa" :class="getMediaIconClass(item)"></i>
                </el-checkbox-button>
              </el-tooltip>
            </el-checkbox-group>
          </div>
          <el-button
            class="media-search-btn"
            icon="fa fa-search"
            @click="searchByMedia(false)"
            size="mini"
          />
        </div>
      </div>

      <div class="ibox-content" style="overflow: hidden">
        <div style="margin: 20px" v-if="settings.instant_poll">
          <h2 id="instant-poll-title">{{ settings.name }}</h2>

          <el-progress
            style="margin-bottom: 20px"
            v-for="result in pollStats"
            :key="result.id"
            :text-inside="true"
            :stroke-width="26"
            :format="percentage => `${result.choice} - ${result.votes} votes - ${percentage}%`"
            :color="generateTitleColor(result.choice)"
            :percentage="instantPollVotePercentage(result.votes)"
          >
          </el-progress>

          <div id="instant-profile-warning">
            This panel will disappear 3 hours after the instant poll closes. The results can be
            accessed at any time under the Campaigns menu. <br />If this menu isn't available please
            contact your administrator.
          </div>
        </div>
        <perfect-scrollbar class="ibox-content" :ref="'content'" @ps-scroll-y="onScroll">
          <div :style="{ height: `${this.topHeight}px` }"></div>
          <div class="placeholder" v-if="loadingTop">
            <content-placeholder key="top" :count="2" />
          </div>
          <div v-for="cluster in visibleClusters" :key="cluster.id">
            <!-- <transition-group name="message" mode="out-in" type="animation"> -->
            <template v-if="type != 'twittertrends'">
              <div v-for="interaction in cluster.interactions" :key="interaction.id">
                <message
                  class="message"
                  v-if="
                    interaction.channel != 'survey' && !interaction.message.startsWith('sha256:')
                  "
                  :audio="interaction.audio"
                  :audioTranscript="getAudioTranscript(interaction.audioTranscript)"
                  :avatar="interaction.avatar"
                  :channel="interaction.channel"
                  :columns="(columns && +columns) || 1"
                  :class="{ 'first-message': interaction.id === firstInteractionId }"
                  :comments="interaction.comments"
                  :datetime="interaction.datetime"
                  :datetimeIsChanged="datetimeIsChanged"
                  :displayName="interaction.displayName"
                  :id="interaction.id"
                  :threadId="interaction.threadId"
                  :images="interaction.images"
                  :isOnAir="type === 'onair'"
                  :message="interaction.message"
                  :onAirUsers="onAirUsers"
                  :originator="interaction.originator"
                  :originatorType="interaction.originatorType"
                  :parentInteraction="interaction.parent"
                  :replies="interaction.replies"
                  :sentOnAirBy="interaction.sentBy"
                  :video="interaction.video"
                  :document="interaction.document"
                  :text="interaction.text"
                  :sentiment="interaction.sentiment"
                  :shouldPause="shouldPause(interaction.id)"
                  :inCount="getThreadCount(interaction.threadId, 'in')"
                  :outCount="getThreadCount(interaction.threadId, 'out')"
                  :mediaPreload="mediaPreload"
                  @changeDatetimePostFormat="changeDateFormat"
                  @heightUpdated="heightUpdated"
                  @mediaStarted="mediaStarted(interaction.id)"
                  @mediaEnded="mediaEnded(interaction.id)"
                  @removeOnAir="$emit('removeOnAir', [id, interaction.onAirId, interaction.id])"
                  @onOpenProfile="
                    (originatorType, originator) =>
                      $emit('onOpenProfile', originatorType, originator)
                  "
                />
              </div>
            </template>

            <template v-if="type === 'twittertrends'">
              <topic
                v-for="(topic, index) in twitterTrends"
                :key="index"
                :index="index"
                :topic="topic"
              />
            </template>
            <!-- </transition-group> -->
          </div>
          <div v-if="visibleClusters.length == 0 && !(loading || isLoading)">
            <content-placeholder
              key="bottom"
              :count="1"
              :text-only="true"
              :text-value="'No Interactions'"
            />
          </div>
          <div
            class="placeholder"
            v-if="isLoading && (hasMoreInteractions || !interactions.length)"
            :class="{ 'initial-placeholder': !interactions.length }"
          >
            <content-placeholder key="bottom" :count="interactions.length ? 1 : 3" />
          </div>
          <div class="placeholder" v-if="interactions.length && !hasMoreInteractions">
            <content-placeholder
              key="bottom"
              :count="1"
              :text-only="true"
              :text-value="'End of Interactions'"
            />
          </div>
          <div :style="{ height: `${this.bottomHeight}px` }"></div>
        </perfect-scrollbar>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { chunk, flatten, round } from 'lodash';
import { take } from 'pipe-operator';

import * as types from '@/store/modules/interaction/mutation-types';

import { PerfectScrollbar } from 'vue2-perfect-scrollbar';

import ContentPlaceholder from '@/components/common/ContentPlaceholder';
import Message from '@/components/interaction/Message';
import Topic from '@/components/interaction/Topic';
import colors from '@/mixins/colors';

const INSTANT_POLL_POLLING_SECS = 10;

export default {
  name: 'MessagePanel',
  components: {
    ContentPlaceholder,
    PerfectScrollbar,
    Message,
    Topic,
  },
  mixins: [colors],
  data: () => ({
    loadingTop: false,
    isLoading: false,
    scrolling: false,
    panelMediaIsPlaying: [],
    clusters: [],
    clusterCount: 3,
    clusterInteractions: 20,
    clusterGroup: 0,
    messageHeights: {},
    lastScrollPosition: 0,
    switchHeight: null,
    checkScrollInterval: null,
    hasNewMessages: false,
    lastInteractionId: 0,
    showPanelSearch: false,
    showMediaSearch: false,
    isSearching: false,
    searchCriteria: null,
    scrollCheckInterval: null,
    scrollBarSettings: {
      suppressScrollX: true,
    },
    mediaTypes: ['Image', 'Video', 'Audio'],
    mediaSearchCriteria: [],
    datetimeIsChanged: null,
    trendInterval: null,
    instantPollInterval: null,
  }),
  props: {
    id: {
      type: String,
      required: true,
    },
    isPaused: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    color: {
      type: String,
      default: null,
    },
    columns: {
      type: String,
      default: null,
    },
    canClose: {
      type: Boolean,
      default: true,
    },
    hasSettings: {
      type: Boolean,
      default: true,
    },
    interactions: {
      type: Array,
      default: () => [],
    },
    twitterTrends: {
      type: Array,
      default: () => [],
    },
    onAirUsers: {
      type: Array,
      default: () => [],
    },
    goToLastScrollPosition: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    loadingSuccess: {
      type: Boolean,
      required: true,
    },
    hasMoreInteractions: {
      type: Boolean,
      default: true,
    },
    isFixedPanel: {
      type: Boolean,
      default: false,
    },
    hasSearch: {
      type: Boolean,
      default: true,
    },
    pollStats: {
      type: Array,
      default: () => [],
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    channels: {
      type: Array,
      default: () => [],
    },
    campaigns: {
      type: Array,
      default: () => [],
    },
    mediaPreload: {
      type: String,
      default: 'auto',
    },
    panelLock: {
      type: String,
      default: 'auto',
    },
  },
  mounted() {
    this.generateClusters();
    this.unpausePanel();

    if (localStorage.getItem('changeDatetimePostFormat')) {
      this.datetimeIsChanged = JSON.parse(localStorage.getItem('changeDatetimePostFormat'));
    }

    if (this.settings.instant_poll) {
      // Instant polls are only linked to a single campaign id
      const campaignId = this.settings.campaign_ids[0];
      this.instantPollInterval = setInterval(() => {
        this.$store.dispatch(
          'interaction/fetchInstantPollStats',
          { panelId: this.id, campaignId },
          { root: true },
        );
      }, 1000 * INSTANT_POLL_POLLING_SECS);
    }

    // if (this.type === 'twittertrends') {
    //   this.trendInterval = setInterval(() => {
    //     this.$store.dispatch('interaction/fetchTwitterTrends');
    //   }, 1200000); // update twitter trends every 20 minutes
    // }
  },

  destroyed() {
    this.pausePanel();
    this.scrollCheckInterval = clearInterval(this.scrollCheckInterval);
    this.$store.commit(`interaction/${types.CLEAR_PANEL_PAUSED}`, this.id);
    clearInterval(this.trendInterval);
    clearInterval(this.instantPollInterval);
  },

  computed: {
    ...mapState('interaction', ['threadCounts']),

    mediaIsPlaying: {
      get() {
        return this.$store.state.interaction.mediaIsPlaying;
      },
      set(mediaIsPlaying) {
        this.$store.commit(`interaction/${types.SET_MEDIA_IS_PLAYING}`, mediaIsPlaying);
      },
    },

    firstInteractionId() {
      return this.interactions[0].id;
    },

    totalInstantPollResults() {
      return this.pollStats.map(result => result.votes).reduce((a, b) => a + b, 0);
    },

    visibleClusters() {
      if (this.clusters.length <= this.clusterCount) {
        return this.clusters;
      }

      let start = this.clusterGroup;
      if (start < 0) {
        start = 0;
      }

      if (start > this.clusters.length) {
        return this.clusters.slice(this.clusters.length - this.clushterCount);
      }

      const end = start + this.clusterCount;
      return this.clusters.slice(start, end);
    },

    topHeight() {
      return this.totalHeightOfInteractions(this.topInvisibleInteractions());
    },

    bottomHeight() {
      return this.totalHeightOfInteractions(this.bottomInvisibleInteractions());
    },

    panelColor() {
      return this.color || this.generateTitleColor(this.id, [0, 360], [0.6, 1], [0.5, 0.5]);
    },

    showSettings() {
      return this.hasSettings;
    },

    showClose() {
      return this.canClose;
    },

    isLocked() {
      return this.panelMediaIsPlaying.length > 0;
    },

    lastInteractionHeight() {
      if (!this.interactions || this.interactions.length === 0) return this.averageHeight();
      return this.totalHeightOfInteractions(this.interactions.slice(0, 1));
    },

    getColumnLayoutClass() {
      return this.columns ? `column-layout-${this.columns}` : null;
    },

    sortingKey() {
      return this.type === 'onair' ? 'onAirId' : 'id';
    },

    panelLocker() {
      return this.panelLock;
    },
  },

  methods: {
    changeDateFormat(date) {
      this.datetimeIsChanged = date;
      const parsed = JSON.stringify(this.datetimeIsChanged);
      localStorage.setItem('changeDatetimePostFormat', parsed);
    },

    isScrolling() {
      if (this.interactions && this.interactions.length === 0) return false;
      if (!this.$refs.content) return false;
      return this.$refs.content.$el.scrollTop > this.lastInteractionHeight;
    },

    totalHeightOfInteractions(interactions) {
      const averageHeight = this.averageHeight();
      return interactions
        .map(interaction => this.messageHeights[interaction.id] || averageHeight)
        .reduce((a, b) => a + b, 0);
    },

    averageHeight() {
      const validHeights = Object.values(this.messageHeights).filter(height => height > 0);

      if (!validHeights.length) {
        return 0;
      }

      const sumHeights = validHeights.reduce((a, b) => a + b, 0);
      return sumHeights / validHeights.length;
    },

    heightUpdated([interactionId, height]) {
      this.messageHeights[interactionId] = height;
    },

    loadMore() {
      if (!this.isLoading) {
        this.isLoading = true;
        if (this.isSearching && this.searchCriteria) {
          this.$emit('searchPanel', this.id, true, this.searchCriteria);
        } else if (this.isSearching && this.mediaSearchCriteria.length) {
          this.searchByMedia(true);
        } else if (this.hasMoreInteractions) {
          this.$emit('loadMore');
        }
      }
    },

    generateClusters() {
      if (this.type === 'twittertrends') {
        this.lastInteractionId = this.twitterTrends.length > 0 && this.twitterTrends[0].name;
        this.hasNewMessages = false;

        const trendChunks = chunk(this.twitterTrends, this.clusterInteractions);

        this.clusters = trendChunks.map((twitterTrends, id) => ({
          id,
          twitterTrends,
        }));
      } else {
        this.lastInteractionId = this.interactions.length > 0 && this.interactions[0].id;
        this.hasNewMessages = false;

        const interactionChunks = chunk(
          this.interactions.sort((a, b) => b[this.sortingKey] - a[this.sortingKey]),
          this.clusterInteractions,
        );

        this.clusters = interactionChunks.map((interactions, id) => ({
          id,
          interactions,
        }));
      }
      this.$forceUpdate();
    },

    /**
     * Make the first cluster visible
     */
    firstCluster() {
      this.clusterGroup = 0;
      this.switchHeight = null;
      this.$forceUpdate();
    },

    /**
     Make the next cluster visible
    */
    nextCluster() {
      this.clusterGroup += 1;
      this.switchHeight = null;
      this.$forceUpdate();
    },

    /**
     Make the previous cluster visible
    */
    prevCluster() {
      this.clusterGroup -= 1;

      if (this.clusterGroup < 0) {
        this.clusterGroup = 0;
      }

      this.switchHeight = null;
      this.$forceUpdate();
    },

    /**
     Flatten a set of clusters into interactions
    */
    flattenClusters(clusters) {
      return flatten(clusters.map(cluster => cluster.interactions));
    },

    /**
     Get all interactions that are not within a visible cluster at the
     top of the panel.
    */
    topInvisibleInteractions() {
      const start = 0;
      const end = this.clusterGroup;

      return this.flattenClusters(this.clusters.slice(start, end));
    },

    /**
     Get all interactions that are not within a visible cluster at the
     bottom of the panel.
    */
    bottomInvisibleInteractions() {
      const start = this.clusterGroup + this.clusterCount;
      const end = this.clusters.length;

      return this.flattenClusters(this.clusters.slice(start, end));
    },

    switchAtHeight(clusterGroup) {
      if (!this.switchHeight) {
        const start = 0;
        const end = clusterGroup;

        this.switchHeight =
          take(this.clusters.slice(start, end))
            .pipe(this.flattenClusters)
            .pipe(this.totalHeightOfInteractions)
            .result() + 12;
      }
      return this.switchHeight;
    },

    onScroll() {
      if (this.lastScrollPosition === this.$refs.content.$el.scrollTop) return;

      const scrollHeight =
        this.$refs.content.$el.scrollHeight - this.$refs.content.$el.clientHeight;
      const scrollPosition = this.$refs.content.$el.scrollTop;
      const switchAtHeight = this.switchAtHeight(this.clusterGroup + 1);
      const scrollDirection = this.lastScrollPosition < scrollPosition ? 'down' : 'up';
      const loadMoreAt =
        scrollHeight - this.totalHeightOfInteractions(this.interactions.slice(-10));

      this.scrolling = this.isScrolling();

      if (
        this.lastScrollPosition > this.lastInteractionHeight &&
        scrollPosition <= this.lastInteractionHeight
      ) {
        if (this.isPaused && !this.isSearching) this.unpausePanel();
        this.generateClusters();
      }

      // Switch to the next cluster
      if (scrollPosition > switchAtHeight && scrollDirection === 'down') {
        this.nextCluster();
        this.switchHeight = null;
      }

      // Switch to the previous cluster
      if (scrollPosition < switchAtHeight && scrollDirection === 'up') {
        this.prevCluster();
        this.switchHeight = null;
      }

      // Should we load more interactions
      if (scrollPosition > loadMoreAt && scrollDirection === 'down' && !this.isLoading) {
        this.loadMore();
      }

      this.lastScrollPosition = scrollPosition;
    },

    scrollToTop() {
      this.$refs.content.$el.scrollTop = 0;
      this.lastScrollPosition = 0;
      this.firstCluster();
    },

    scrollNewMessages() {
      this.scrolling = false;
      this.generateClusters();
      this.scrollToTop();
    },

    shouldPause(interactionId) {
      const lastIndex = this.mediaIsPlaying.length - 1;
      const interactionIndex = this.mediaIsPlaying.indexOf(interactionId);
      return interactionIndex >= 0 && interactionIndex < lastIndex;
    },

    mediaStarted(interactionId) {
      this.mediaIsPlaying = this.mediaIsPlaying.concat(interactionId);
      this.panelMediaIsPlaying = this.panelMediaIsPlaying.concat(interactionId);
      this.pausePanel();
    },

    mediaEnded(interactionId) {
      this.mediaIsPlaying = this.mediaIsPlaying.filter(id => interactionId !== id);
      this.panelMediaIsPlaying = this.panelMediaIsPlaying.filter(id => interactionId !== id);
      this.unpausePanel();
    },

    pausePanel() {
      this.$emit('pausePanel');
    },

    unpausePanel() {
      this.$emit('unpausePanel');
    },

    resetPanel() {
      this.isSearching = false;
      this.$emit('resetPanel');
      this.unpausePanel();
    },

    toggleSearch() {
      if (this.showMediaSearch) this.showMediaSearch = false;
      this.showPanelSearch = !this.showPanelSearch;
      if (this.isSearching && !this.showPanelSearch) {
        this.resetPanel();
      }
    },

    toggleMediaSearch() {
      if (this.showPanelSearch) this.showPanelSearch = false;
      this.showMediaSearch = !this.showMediaSearch;
      if (this.isSearching && !this.showPanelSearch) {
        this.resetPanel();
      }
    },

    clearPanelSearch(clearCriteria) {
      if (clearCriteria) {
        this[clearCriteria] = clearCriteria === 'searchCriteria' ? null : [];
      }

      if (this.isPaused) {
        this.isSearching = false;
        this.scrolling = false;
        this.$store.commit(`interaction/${types.CLEAR_PANEL_HAS_MORE_INDICATOR}`, this.id);
        this.scrollToTop();
        this.unpausePanel();
      }
    },

    searchPanelInteractions() {
      if (!this.searchCriteria) return;

      this.pausePanel();
      this.isSearching = true;
      this.$emit('searchPanel', this.id, false, this.searchCriteria);
    },

    searchByMedia(nextPage) {
      if (!this.mediaSearchCriteria.length) return;

      this.pausePanel();
      this.isSearching = true;

      const query = this.mediaSearchCriteria.reduce(
        (a, x) => ({
          or: [...a.or, { field: x.toLowerCase(), match: 'exists' }],
        }),
        { or: [] },
      );

      this.$emit('searchPanel', this.id, !!nextPage, query);
    },

    getThreadCount(threadId, direction) {
      if (!Object.prototype.hasOwnProperty.call(this.threadCounts, threadId)) {
        return 0;
      }
      return this.threadCounts[threadId][direction];
    },

    getMediaIconClass(item) {
      if (item === 'Image') return 'fa-picture-o';
      if (item === 'Audio') return 'fa-headphones';
      return 'fa-video-camera';
    },

    getSearchBtnIcon(type) {
      if (type === 'media') {
        return this.showMediaSearch ? 'fa fa-times-circle-o' : 'fa fa-image';
      }
      return this.showPanelSearch ? 'fa fa-times-circle-o' : 'fa fa-search';
    },

    incomingInteractions() {
      if (
        this.scrolling &&
        this.interactions.length > 0 &&
        this.lastInteractionId < this.interactions[0].id
      ) {
        this.hasNewMessages = true;
        return;
      }

      this.loadingTop = false;
      this.generateClusters();
    },

    instantPollVotePercentage(votes) {
      if (this.totalInstantPollResults === 0) return 0;
      return round((votes / this.totalInstantPollResults) * 100, 2);
    },

    getAudioTranscript(additionalArray) {
      if (!Array.isArray(additionalArray)) return [];

      return additionalArray
        .filter(item => item && item.transcription && item.transcription.length > 0)
        .map(item => item.transcription[0]);
    },
  },

  watch: {
    loadingSuccess(val) {
      if (val) this.incomingInteractions();
    },

    interactions() {
      if (!this.scrolling || this.isLoading) {
        this.generateClusters();
        if (this.isLoading) this.isLoading = false;
      }
    },

    loading(val) {
      if (!val) this.isLoading = false;
    },

    goToLastScrollPosition(shouldScroll) {
      if (!shouldScroll) {
        return;
      }
      // Dragging and dropping resets scroll position,
      // lets set it back to it's last known value
      this.$refs.content.$el.scrollTop = this.lastScrollPosition;
    },

    showPanelSearch(newVal) {
      if (!newVal) {
        this.clearPanelSearch('searchCriteria');
      }
    },

    showMediaSearch(newVal) {
      if (!newVal) {
        this.clearPanelSearch('mediaSearchCriteria');
      }
    },
  },
};
</script>

<style src="vue2-perfect-scrollbar/dist/vue2-perfect-scrollbar.css" />

<style lang="scss" scoped>
@import '../../assets/style/_variables_live.scss';
@import '../../assets/style/animate.css';

.message-enter-active {
  animation: bounceInRight 1s ease forwards;
  transition: opacity 0.8s;
  background-color: white;
}

.message-enter {
  opacity: 0;
}

.message-move {
  transition: transform 0.8s;
  background-color: white;
}

.message-panel-container {
  height: calc(100vh - 125px);

  &.profile-container {
    max-height: 500px;
    padding-bottom: 15px;
    margin-bottom: 3px;
  }

  &.fixed-container {
    width: 100%;
  }
}

.message-panel {
  width: 400px;
  height: 95%;
  position: static;

  &.fixed {
    width: 100%;
    margin-top: 0;
  }

  &.column-layout-1 {
    width: 400px;
  }

  &.column-layout-2 {
    width: 800px;
  }

  &.column-layout-3 {
    width: 1200px;
  }

  .ibox-content {
    padding: 0;
    height: 100%; // prevent ibox-title scrolling off screen
    overflow-y: scroll;
    overflow-x: hidden;
    scroll-behavior: auto;
  }

  .ibox-title {
    h5 {
      font-size: 0.95em;
    }
    .drag-indicator {
      position: relative;
      margin-top: -8px;
      margin-bottom: -8px;
      font-size: 10px;
      color: $colony-light-gray;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .ibox-tools {
      margin-top: -3px;
      cursor: grab;

      button {
        color: inherit;
        margin: -10px 2px;
      }
    }
  }

  .first-message {
    padding-top: 0 !important;
    margin-top: 0;
    border-top: none !important;
  }

  .message {
    padding: 12px;
    margin-top: 12px;
    border-top: 1px solid #eee;
  }

  .placeholder {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #eee;
  }

  .initial-placeholder {
    border-top: none;
  }

  .locked {
    color: $colony-danger;
  }
}

.media-search {
  margin-top: 3px;
  max-width: 75%;
}

.media-search-chk {
  float: left;
}

.media-search-btn {
  max-width: 15%;
  margin-left: 8px;
}

.ibox {
  border: 0;
  box-shadow: 0;
}

.message-panel-container {
  background: transparent;
  margin-top: 10px;
  margin-bottom: 10px;
  border: 1px solid #e9e9e9;
  box-shadow: 1px 1px 7px 0 #ddd;
}

#instant-poll-title {
  text-align: center;
  margin-top: 30px;
  margin-bottom: 30px;
}

#instant-profile-warning {
  text-align: center;
  padding: 10px 10px;
  font-size: 0.8em;
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// MessagePanel.vue?280e48f4